<script setup>
/* eslint semi: 1 */
import { ref, watch } from 'vue';
import { FwbInput } from 'flowbite-vue';
import { useMediaQuery } from '@/composables/media/use-media-query';

const query = useMediaQuery();

const emit = defineEmits(['apply', 'clear']);

// Local draft for the search text input. This is what
// the user is currently typing before they click "Apply".
const searchText = ref('');

// Keep the local draft in sync with committed query state.
watch(
  query.searchText,
  (val) => { searchText.value = val ?? ''; },
  { immediate: true }
);

/**
 * Commits the current search text to the query layer (persists to URL +
 * localStorage and updates `apiParams`).
 * Call this when the user confirms — e.g. on input `@change` or Enter.
 */
function onApply () {
  if (searchText.value === query.searchText.value) {
    // No change, no need to apply.
    return;
  }

  query.applySearchText(searchText.value);
  emit('apply');
}

/**
 * Clears both the local draft and the committed query state.
 * Removes the `searchText` URL param.
 */
function onClear () {
  searchText.value = '';
  query.clearSearchText();
  emit('clear');
}
</script>

<template>
  <FwbInput
    v-model.trim="searchText"
    ref="inputRef"
    :placeholder="$t('label.search')"
    data-component-element-id="media-search-filter-input"
    class="w-60 rounded-lg"
    input-class="rounded-lg block focus:outline-2 focus:outline-solid focus:outline-transparent focus:outline-offset-0 pr-7"
    size="sm"
    @change="onApply"
    @keyup.enter="onApply"
    @blur="onApply"
  >
    <template #prefix>
      <BaseIconSvg
        class="h-5! w-5! text-gray-500"
        name="flowbite:search-outline"
      />
    </template>

    <template #suffix>
      <button
        v-if="searchText?.length > 0"
        type="button"
        class="absolute top-1 right-1.5 h-7 w-7 flex items-center cursor-pointer rounded-4xl bg-transparent hover:bg-gray-300 border-0!"
        data-component-element-id="media-search-filter-input-clear"
        :aria-label="$t('buttons.clearAll')"
        @click="onClear"
      >
        <BaseIconSvg
          name="flowbite:close-outline"
          class="w-4! h-4!"
        />
      </button>
    </template>
  </FwbInput>
</template>
